import React, { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { adminApi } from "@/lib/adminApi";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from "@/components/ui/dialog";
import { invalidateTestimonials } from "@/lib/useTestimonials";

const EMPTY = {
    quote: "",
    author_name: "",
    author_title: "",
    organization: "",
    industry: "",
    initials: "",
    published: true,
    order: 0,
};

const TestimonialsManager = () => {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(null);
    const [saving, setSaving] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await adminApi.get("/admin/testimonials");
            setItems(data);
        } catch {
            toast.error("Could not load testimonials.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    const save = async () => {
        if (!editing) return;
        setSaving(true);
        try {
            const body = { ...editing, order: Number(editing.order) || 0 };
            if (editing.id) {
                await adminApi.put(`/admin/testimonials/${editing.id}`, body);
            } else {
                await adminApi.post("/admin/testimonials", body);
            }
            invalidateTestimonials();
            toast.success("Saved.");
            setEditing(null);
            await load();
        } catch (e) {
            toast.error("Save failed.");
        } finally {
            setSaving(false);
        }
    };

    const del = async (id) => {
        if (!window.confirm("Delete this testimonial?")) return;
        try {
            await adminApi.delete(`/admin/testimonials/${id}`);
            invalidateTestimonials();
            toast.success("Deleted.");
            await load();
        } catch {
            toast.error("Delete failed.");
        }
    };

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <div>
                    <h2 className="font-montserrat font-bold text-indigo_brand text-2xl">Testimonials</h2>
                    <p className="font-opensans text-sm text-[#4A5568] mt-1">
                        Rendered on the home page and other marketing pages. Unpublish to hide without deleting.
                    </p>
                </div>
                <button onClick={() => setEditing({ ...EMPTY })} className="ih-btn-primary" data-testid="testimonials-new-btn">
                    <Plus size={16}/> New Testimonial
                </button>
            </div>

            {loading ? (
                <div className="ih-card p-10 text-center text-[#4A5568]"><Loader2 className="inline animate-spin mr-2" size={16}/>Loading…</div>
            ) : items.length === 0 ? (
                <div className="ih-card p-10 text-center text-[#4A5568]">No testimonials yet.</div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {items.map((t) => (
                        <div key={t.id} data-testid={`testimonial-row-${t.id}`} className="ih-card p-6">
                            <blockquote className="font-opensans text-sm text-[#1A2E4F] leading-relaxed">
                                &ldquo;{t.quote}&rdquo;
                            </blockquote>
                            <div className="mt-4 flex items-center justify-between text-xs">
                                <div>
                                    <div className="font-semibold text-indigo_brand">{t.author_name}</div>
                                    <div className="text-[#4A5568]">{t.author_title}{t.organization ? ` · ${t.organization}` : ""}</div>
                                </div>
                                <div className="flex items-center gap-2">
                                    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.14em] ${
                                        t.published ? "bg-green_brand/15 text-green_brand" : "bg-[#F5F5F5] text-[#4A5568]"
                                    }`}>{t.published ? "Published" : "Draft"}</span>
                                </div>
                            </div>
                            <div className="mt-4 flex gap-2">
                                <button onClick={() => setEditing(t)} className="ih-btn-ghost" data-testid={`testimonial-edit-${t.id}`}><Pencil size={14}/>Edit</button>
                                <button onClick={() => del(t.id)} className="ih-btn-ghost text-red-600"><Trash2 size={14}/>Delete</button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
                <DialogContent className="max-w-lg">
                    <DialogHeader>
                        <DialogTitle>{editing?.id ? "Edit testimonial" : "New testimonial"}</DialogTitle>
                    </DialogHeader>
                    {editing && (
                        <div className="space-y-3">
                            <div>
                                <Label>Quote</Label>
                                <Textarea rows={4} value={editing.quote} onChange={(e) => setEditing({ ...editing, quote: e.target.value })} data-testid="testimonial-quote" />
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <Label>Author name</Label>
                                    <Input value={editing.author_name} onChange={(e) => setEditing({ ...editing, author_name: e.target.value })} />
                                </div>
                                <div>
                                    <Label>Author title</Label>
                                    <Input value={editing.author_title || ""} onChange={(e) => setEditing({ ...editing, author_title: e.target.value })} />
                                </div>
                                <div>
                                    <Label>Organization</Label>
                                    <Input value={editing.organization || ""} onChange={(e) => setEditing({ ...editing, organization: e.target.value })} />
                                </div>
                                <div>
                                    <Label>Industry</Label>
                                    <Input value={editing.industry || ""} onChange={(e) => setEditing({ ...editing, industry: e.target.value })} />
                                </div>
                                <div>
                                    <Label>Initials</Label>
                                    <Input maxLength={4} value={editing.initials || ""} onChange={(e) => setEditing({ ...editing, initials: e.target.value })} />
                                </div>
                                <div>
                                    <Label>Order</Label>
                                    <Input type="number" value={editing.order} onChange={(e) => setEditing({ ...editing, order: e.target.value })} />
                                </div>
                            </div>
                            <div className="flex items-center gap-3">
                                <Switch checked={editing.published} onCheckedChange={(v) => setEditing({ ...editing, published: v })} data-testid="testimonial-published" />
                                <Label>Published</Label>
                            </div>
                        </div>
                    )}
                    <DialogFooter>
                        <button onClick={() => setEditing(null)} className="ih-btn-ghost">Cancel</button>
                        <button onClick={save} disabled={saving} className="ih-btn-primary disabled:opacity-60" data-testid="testimonial-save">
                            {saving ? <><Loader2 className="animate-spin" size={14}/> Saving…</> : "Save"}
                        </button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default TestimonialsManager;
